<script setup lang="ts">
import { computed, watch } from 'vue';
import { DgrSelectbox } from '@stockmarkteam/donguri-ui';
import TagSelect from '@/components/common/multi-select/tag-select.vue';
import { IdentifyValue, TagType } from '@/types';
import { useGroups, useGroupTagsNonSticky } from '@/utils/swr';
import SurveyFilterTag from './survey-filter-tag.vue';

const groupId = defineModel<number | undefined>('id');
const groupTagIds = defineModel<number[]>('tags');

// グループ
const { data: groups } = useGroups();
watch(
  groups,
  () => {
    if (!groups.value || groupId.value !== undefined) return;

    // 全体グループを初期選択にする
    const firstGroup = groups.value.groups.find(
      g => g.group_type === 'all_user_group',
    );
    if (firstGroup) {
      groupId.value = firstGroup.id;
    }
  },
  { immediate: true },
);

const groupOptions = computed(() => {
  if (!groups.value) return [];
  return groups.value.groups
    .filter(group => group.is_member)
    .map(group => ({
      label: group.name,
      value: group.id,
    }));
});

// グループタグ
const { data: groupTags } = useGroupTagsNonSticky(groupId);
const tagList = computed(() => {
  return groupTags.value?.tags ?? [];
});

const groupTagIdToName = computed(() => {
  return Object.fromEntries(tagList.value.map(tag => [tag.id, tag.name]));
});

const groupTagNameToId = computed(() => {
  return Object.fromEntries(tagList.value.map(tag => [tag.name, tag.id]));
});

const selectedTagNames = computed(() => {
  return groupTagIds.value?.map(id => groupTagIdToName.value[id]) ?? [];
});

const tagSelected = (tagNames: string[]) => {
  groupTagIds.value = tagNames.map(name => groupTagNameToId.value[name]);
};

const removeGroupTag = (value: {
  filterType: TagType;
  identifyValue: IdentifyValue;
}) => {
  const tagId = value.identifyValue as number;
  const index = groupTagIds.value?.indexOf(tagId) ?? -1;
  if (index !== -1) {
    groupTagIds.value?.splice(index, 1);
  }
};

watch(groupId, () => {
  // グループ変更時に選択済みタグをリセット
  groupTagIds.value = [];
});
</script>

<template>
  <div>
    <div class="section-title c-text-regular--m">参加済みグループ</div>
    <DgrSelectbox
      v-model="groupId"
      :options="groupOptions"
      :disabled="groupOptions.length === 0"
      :placeholder="''"
      class="group-select"
    />
    <div class="section-title c-text-regular--m">グループタグ</div>
    <TagSelect
      size="medium"
      placeholder="グループタグ"
      :disabled="tagList.length === 0"
      :tag-list="tagList"
      :target-tags="selectedTagNames"
      :is-show-count="true"
      @filter-articles="tagSelected"
    />
    <div class="tag-container">
      <SurveyFilterTag
        v-for="tagId in groupTagIds"
        :key="tagId"
        type="groupTags"
        :tag-label="groupTagIdToName[tagId]"
        :identify-value="tagId"
        :enable-delete="true"
        @delete-tag="removeGroupTag"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.section-title {
  margin: 16px 0 4px 0;
}

.group-select {
  width: 360px;
}

.tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;
}
</style>
